import React, { useEffect, useMemo, useState, useRef } from "react";
import { Select, Row, Col } from "antd";
import ReactGA from "react-ga4";
import { useMakes } from "../../../routes/Configuration/hooks";
import { filterMakesBySubcategories } from "../../../routes/Configuration/helpers";
import { CategoryName } from "../../../models/enums";
import {
  getWindowWidth,
  isSearchMobile,
  matchesSearchText
} from "../../../services";
import styles from "./style.scss";
import MultiSelectModal from "../MultiSelectModal";

const { Option, OptGroup } = Select;

// Multi select of makes and models of one category (search page). Makes are
// fetched for the category the control is configured in. `subcategoryIds`
// (selected subcategories of the filter, see searchFilterConditions) narrows
// them to the makes and models offered in those subcategories.
const MultiMakeSelector = (props) => {
  const {
    category,
    child,
    onChange,
    value,
    data,
    showPlaceholder,
    getPopupContainer,
    isColumn,
    subcategoryIds
  } = props;

  // Use data prop as fallback if value is not available (for initial load)
  const initialData = value || data;

  const [selectedMakeIds, setSelectedMakeIds] = useState([]);
  const [selectedModelIds, setSelectedModelIds] = useState([]);
  const [isMakeModalVisible, setIsMakeModalVisible] = useState(false);
  const [isModelModalVisible, setIsModelModalVisible] = useState(false);
  const [isMakeDropdownOpen, setIsMakeDropdownOpen] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const isInitialized = useRef(false);
  const makeSelectRef = useRef(null);
  const modelSelectRef = useRef(null);

  const isMobile = isSearchMobile(getWindowWidth());
  const hasModelField = !!(child && child.child && child.child.field);

  // makes of the control's category, with their models
  const allMakes = useMakes(category);
  const subcategoryKey = (subcategoryIds || []).join(",");
  const makes = useMemo(
    () => filterMakesBySubcategories(allMakes, subcategoryIds),
    [allMakes, subcategoryKey]
  );

  // Selections that fell out of the list after the subcategories changed are dropped
  useEffect(() => {
    if (!isInitialized.current || !makes) {
      return;
    }

    const validMakeIds = selectedMakeIds.filter(makeId => makes.some(make => make.id === makeId));
    const validModelIds = selectedModelIds.filter(modelId =>
      makes.some(make => make.models && make.models.some(model => model.id === modelId))
    );

    if (validMakeIds.length === selectedMakeIds.length && validModelIds.length === selectedModelIds.length) {
      return;
    }

    setSelectedMakeIds(validMakeIds);
    setSelectedModelIds(validModelIds);
    emitChange(validMakeIds, validModelIds);
  }, [subcategoryKey]);

  // Emit initial change to parent form (used during initialization)
  const emitInitialChange = (makeIds, modelIds, makeField, modelField) => {
    const newValue = {};

    // Find makes that have models selected
    const makesWithModels = new Set();
    if (makes) {
      modelIds.forEach(modelId => {
        for (const make of makes) {
          if (make.models && make.models.some(m => m.id === modelId)) {
            makesWithModels.add(make.id);
            break;
          }
        }
      });
    }

    // Find makes that are selected but have NO models selected
    const makesWithoutModels = makeIds.filter(makeId => !makesWithModels.has(makeId));

    if (modelIds.length > 0) {
      newValue[modelField] = modelIds;
    }

    if (makesWithoutModels.length > 0) {
      newValue[makeField] = makesWithoutModels;
    }

    if (Object.keys(newValue).length > 0) {
      onChange(newValue);
    }
  };

  // Initialize from props value (only once when resources are loaded)
  useEffect(() => {
    // Only initialize once
    if (isInitialized.current) {
      return;
    }

    if (!makes) {
      return;
    }

    if (!initialData) {
      isInitialized.current = true;
      return;
    }

    const makeField = child && child.field ? child.field : "includeMakeIds";
    const modelField = child && child.child && child.child.field ? child.child.field : "includeModelIds";

    const modelIds = initialData[modelField];
    const makeIds = initialData[makeField];

    const modelIdArray = modelIds && modelIds.length > 0
      ? (Array.isArray(modelIds) ? modelIds : [modelIds])
      : [];
    const makeIdArray = makeIds && makeIds.length > 0
      ? (Array.isArray(makeIds) ? makeIds : [makeIds])
      : [];

    // If we have model IDs, derive make IDs from them
    if (modelIdArray.length > 0) {
      // Find the makes that contain these models
      const derivedMakeIds = [];
      let foundModelCount = 0;

      makes.forEach(make => {
        if (make.models) {
          const matchingModels = make.models.filter(model =>
            modelIdArray.includes(model.id)
          );
          if (matchingModels.length > 0) {
            foundModelCount += matchingModels.length;
            if (!derivedMakeIds.includes(make.id)) {
              derivedMakeIds.push(make.id);
            }
          }
        }
      });

      // Only mark as initialized if we found all expected models
      // This ensures we wait for all make children to be loaded
      if (foundModelCount === modelIdArray.length) {
        isInitialized.current = true;
        setSelectedModelIds(modelIdArray);

        // Combine derived makes with explicit make IDs (makes without models)
        const allMakeIds = [...derivedMakeIds];
        makeIdArray.forEach(makeId => {
          if (!allMakeIds.includes(makeId)) {
            allMakeIds.push(makeId);
          }
        });
        setSelectedMakeIds(allMakeIds);

        // Emit initial values to parent form
        emitInitialChange(allMakeIds, modelIdArray, makeField, modelField);
      }
    } else if (makeIdArray.length > 0) {
      // Only make IDs provided
      isInitialized.current = true;
      setSelectedMakeIds(makeIdArray);
      setSelectedModelIds([]);

      // Emit initial values to parent form
      emitInitialChange(makeIdArray, [], makeField, modelField);
    } else {
      isInitialized.current = true;
    }
  }, [initialData, makes]);

  // Get makes (brands) sorted alphabetically
  const getMakes = () => {
    if (!makes) {
      return [];
    }
    return [...makes].sort((a, b) =>
      a.name.localeCompare(b.name, 'hr')
    );
  };

  // Get popular makes and remaining makes separately
  const getGroupedMakes = () => {
    const allMakes = getMakes();
    const popularMakes = allMakes.filter(m => m.popular);
    const otherMakes = allMakes.filter(m => !m.popular);
    return { popularMakes, otherMakes };
  };

  // Determine GA label based on the category
  const getGaLabel = () => {
    if (category === CategoryName.car) return "Top marke u autima";
    if (category === CategoryName.motorbike) return "Top marke u motorima";
    return "Top marke u autobusima";
  };

  // Track GA event for popular make selection
  const trackPopularMakeSelection = (makeIds) => {
    const { popularMakes } = getGroupedMakes();
    const popularIds = new Set(popularMakes.map(m => m.id));
    const hasPopularSelection = makeIds.some(id => popularIds.has(id));
    if (hasPopularSelection) {
      ReactGA.event({
        category: "Odabir marke",
        action: "Click",
        label: getGaLabel()
      });
    }
  };

  // Get models for selected makes (or all makes if none selected), grouped by make and sorted alphabetically
  const getGroupedModels = () => {
    const makes = getMakes();
    const groups = [];

    // If no makes selected, show all makes with models
    const makeIdsToShow = selectedMakeIds.length > 0 ? selectedMakeIds : makes.map(m => m.id);

    makeIdsToShow.forEach(makeId => {
      const make = makes.find(m => m.id === makeId);
      if (make && make.models && make.models.length > 0) {
        const sortedModels = [...make.models].sort((a, b) =>
          a.name.localeCompare(b.name, 'hr')
        );
        groups.push({
          makeName: make.name,
          makeId: make.id,
          models: sortedModels
        });
      }
    });

    return groups;
  };

  // Handle make selection change
  const handleMakeChange = (newMakeIds) => {
    // Track GA for newly selected popular makes
    const newSelections = newMakeIds.filter(id => !selectedMakeIds.includes(id));
    if (newSelections.length > 0) {
      trackPopularMakeSelection(newSelections);
    }

    setSelectedMakeIds(newMakeIds);

    // Remove model selections that are no longer valid
    const makes = getMakes();
    const validModelIds = selectedModelIds.filter(modelId => {
      return newMakeIds.some(makeId => {
        const make = makes.find(m => m.id === makeId);
        return make && make.models && make.models.some(model => model.id === modelId);
      });
    });

    setSelectedModelIds(validModelIds);
    emitChange(newMakeIds, validModelIds);
  };

  // Handle model selection change
  const handleModelChange = (newModelIds) => {
    setSelectedModelIds(newModelIds);
    emitChange(selectedMakeIds, newModelIds);
  };

  // Emit change to parent
  const emitChange = (makeIds, modelIds) => {
    const makeField = child && child.field ? child.field : "includeMakeIds";
    const modelField = child && child.child && child.child.field ? child.child.field : "includeModelIds";

    const newValue = {};
    const makes = getMakes();

    // Find makes that have models selected
    const makesWithModels = new Set();
    modelIds.forEach(modelId => {
      for (const make of makes) {
        if (make.models && make.models.some(m => m.id === modelId)) {
          makesWithModels.add(make.id);
          break;
        }
      }
    });

    // Find makes that are selected but have NO models selected (need to be saved as makeIds)
    const makesWithoutModels = makeIds.filter(makeId => !makesWithModels.has(makeId));

    // Always send both fields to clear stale values from previous selections
    newValue[modelField] = modelIds.length > 0 ? modelIds : undefined;
    newValue[makeField] = makesWithoutModels.length > 0 ? makesWithoutModels : undefined;

    onChange(newValue);
  };

  // Get make options for dropdown
  const getMakeOptions = () => {
    return getMakes().map(make => ({
      title: make.name,
      value: make.id
    }));
  };

  // Get model options for dropdown (flat list with group info)
  const getModelOptions = () => {
    const options = [];
    getGroupedModels().forEach(group => {
      group.models.forEach(model => {
        options.push({
          title: model.name + " (" + group.makeName + ")",
          value: model.id
        });
      });
    });
    return options;
  };

  // Get model options grouped by make (for mobile modal)
  const getModelGroups = () => {
    return getGroupedModels().map(group => ({
      label: group.makeName,
      options: group.models.map(model => ({
        title: model.name,
        value: model.id
      }))
    }));
  };

  // Get make options grouped by popular/other for mobile modal
  const getMakeGroups = () => {
    const { popularMakes, otherMakes } = getGroupedMakes();
    if (popularMakes.length === 0) return null;

    const topMarkeLabel = "Top marke";
    const otherLabel = "Ostale marke";

    const groups = [
      {
        label: topMarkeLabel,
        popular: true,
        options: popularMakes.map(make => ({ title: make.name, value: make.id }))
      },
      {
        label: otherLabel,
        options: otherMakes.map(make => ({ title: make.name, value: make.id }))
      }
    ];
    return groups;
  };

  // Render make options with popular grouping for desktop Select
  const renderMakeSelectOptions = () => {
    const { popularMakes, otherMakes } = getGroupedMakes();
    if (popularMakes.length === 0) {
      return getMakes().map(make => (
        <Option key={make.id} value={make.id}>
          {make.name}
        </Option>
      ));
    }
    const topMarkeLabel = "Top marke";
    const otherLabel = "Ostale marke";
    return [
      <OptGroup key="popular" label={topMarkeLabel}>
        {popularMakes.map(make => (
          <Option key={make.id} value={make.id}>
            {make.name}
          </Option>
        ))}
      </OptGroup>,
      <OptGroup key="other" label={otherLabel}>
        {otherMakes.map(make => (
          <Option key={make.id} value={make.id}>
            {make.name}
          </Option>
        ))}
      </OptGroup>
    ];
  };

  // Render make names for display
  const renderMakeNames = () => {
    const makes = getMakes();
    return selectedMakeIds
      .map(id => {
        const make = makes.find(m => m.id === id);
        return make ? make.name : null;
      })
      .filter(Boolean)
      .join(", ");
  };

  // Render model names for display
  const renderModelNames = () => {
    const makes = getMakes();
    return selectedModelIds
      .map(modelId => {
        for (const make of makes) {
          if (make.models) {
            const model = make.models.find(m => m.id === modelId);
            if (model) return model.name;
          }
        }
        return null;
      })
      .filter(Boolean)
      .join(", ");
  };

  const makeTitle = (child && child.title) || "Marka" || "Marka";
  const modelTitle = (child && child.child && child.child.title) || "Model" || "Model";
  const makePlaceholder = (child && child.placeholder) || "Odaberite";
  const modelPlaceholder = (child && child.child && child.child.placeholder) || "Odaberite";

  // Mobile rendering with modals
  if (isMobile) {
    return (
      <div className={styles.wrapper}>
        <Row gutter={8}>
          <Col xs={24} className={hasModelField ? styles.mobileCol : undefined}>
            {!showPlaceholder && (
              <span className="control-title">{makeTitle}</span>
            )}
            <div
              className={`${styles.mobileSelect} ${selectedMakeIds.length > 0 ? styles.mobileSelectHasValue : ''}`}
              onClick={() => setIsMakeModalVisible(true)}
            >
              {selectedMakeIds.length > 0 ? renderMakeNames() : (showPlaceholder ? makeTitle : makePlaceholder)}
            </div>
            <MultiSelectModal
              title={makeTitle}
              options={getMakeGroups() ? [] : getMakeOptions()}
              groups={getMakeGroups()}
              value={selectedMakeIds}
              visible={isMakeModalVisible}
              onCancel={() => setIsMakeModalVisible(false)}
              onOk={handleMakeChange}
            />
          </Col>

          {hasModelField && (
            <Col xs={24} className={styles.mobileCol}>
              {!showPlaceholder && (
                <span className="control-title">{modelTitle}</span>
              )}
              <div
                className={`${styles.mobileSelect} ${selectedModelIds.length > 0 ? styles.mobileSelectHasValue : ''} ${selectedMakeIds.length === 0 ? styles.mobileSelectDisabled : ''}`}
                onClick={() => selectedMakeIds.length > 0 && setIsModelModalVisible(true)}
              >
                {selectedModelIds.length > 0 ? renderModelNames() : (showPlaceholder ? modelTitle : modelPlaceholder)}
              </div>
              <MultiSelectModal
                title={modelTitle}
                options={[]}
                groups={getModelGroups()}
                value={selectedModelIds}
                visible={isModelModalVisible}
                onCancel={() => setIsModelModalVisible(false)}
                onOk={handleModelChange}
              />
            </Col>
          )}
        </Row>
      </div>
    );
  }

  // Desktop rendering
  const groupedModels = getGroupedModels();
  const hasModelsAvailable = groupedModels.some(g => g.models.length > 0);

  // Get display value - show comma-separated names when not focused, IDs when focused
  const getMakeDisplayValue = () => {
    if (isMakeDropdownOpen) {
      return selectedMakeIds;
    }
    return selectedMakeIds.length > 0 ? renderMakeNames() : undefined;
  };

  const getModelDisplayValue = () => {
    if (isModelDropdownOpen) {
      return selectedModelIds;
    }
    return selectedModelIds.length > 0 ? renderModelNames() : undefined;
  };

  const handleMakeDropdownVisibleChange = (open) => {
    setIsMakeDropdownOpen(open);
  };

  const handleModelDropdownVisibleChange = (open) => {
    setIsModelDropdownOpen(open);
  };

  // Row layout (side by side) using flexbox
  if (isColumn === false) {
    return (
      <div className={styles.rowWrapper}>
        <div className={styles.fieldItem}>
          {!showPlaceholder && (
            <span className="control-title">{makeTitle}</span>
          )}
          <div style={{ maxHeight: 54 }}>
            <Select
              ref={makeSelectRef}
              mode={isMakeDropdownOpen ? "multiple" : "default"}
              className={styles.select}
              placeholder={showPlaceholder ? makeTitle : makePlaceholder}
              value={getMakeDisplayValue()}
              onChange={handleMakeChange}
              open={isMakeDropdownOpen}
              onDropdownVisibleChange={handleMakeDropdownVisibleChange}
              showSearch
              autoClearSearchValue={false}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option && option.props && option.props.children;
                return typeof text === "string" && matchesSearchText(input, text);
              }}
              notFoundContent={"Nema rezultata"}
              getPopupContainer={getPopupContainer}
            >
              {renderMakeSelectOptions()}
            </Select>
          </div>
        </div>

        {hasModelField && (
          <div className={styles.fieldItem}>
            {!showPlaceholder && (
              <span className="control-title">{modelTitle}</span>
            )}
            <div style={{ maxHeight: 54 }}>
              <Select
                ref={modelSelectRef}
                mode={isModelDropdownOpen ? "multiple" : "default"}
                className={styles.select}
                dropdownClassName={styles.modelDropdown}
                autoClearSearchValue={false}
                placeholder={showPlaceholder ? modelTitle : modelPlaceholder}
                value={getModelDisplayValue()}
                onChange={handleModelChange}
                open={isModelDropdownOpen}
                onDropdownVisibleChange={handleModelDropdownVisibleChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const text = option && option.props && option.props.children;
                  return typeof text === "string" && matchesSearchText(input, text);
                }}
                notFoundContent={"Nema rezultata"}
                getPopupContainer={getPopupContainer}
                disabled={selectedMakeIds.length === 0}
              >
                {groupedModels.map(group => (
                  <OptGroup key={group.makeId} label={group.makeName}>
                    {group.models.map(model => (
                      <Option key={model.id} value={model.id}>
                        {model.name}
                      </Option>
                    ))}
                  </OptGroup>
                ))}
              </Select>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Column layout (stacked) using Row/Col
  return (
    <div className={styles.columnWrapper}>
      <Row gutter={8}>
        <Col xs={24} className={hasModelField ? styles.fieldCol : undefined}>
          {!showPlaceholder && (
            <span className="control-title">{makeTitle}</span>
          )}
          <div style={{ maxHeight: 54, marginBottom: hasModelField ? 10 : 0 }}>
            <Select
              ref={makeSelectRef}
              mode={isMakeDropdownOpen ? "multiple" : "default"}
              className={styles.select}
              placeholder={showPlaceholder ? makeTitle : makePlaceholder}
              value={getMakeDisplayValue()}
              onChange={handleMakeChange}
              open={isMakeDropdownOpen}
              onDropdownVisibleChange={handleMakeDropdownVisibleChange}
              showSearch
              autoClearSearchValue={false}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option && option.props && option.props.children;
                return typeof text === "string" && matchesSearchText(input, text);
              }}
              notFoundContent={"Nema rezultata"}
              getPopupContainer={getPopupContainer}
            >
              {renderMakeSelectOptions()}
            </Select>
          </div>
        </Col>

        {hasModelField && (
          <Col xs={24} className={styles.fieldCol}>
              {!showPlaceholder && (
                <span className="control-title">{modelTitle}</span>
              )}
              <div style={{ maxHeight: 54 }}>
                <Select
                  ref={modelSelectRef}
                  mode={isModelDropdownOpen ? "multiple" : "default"}
                  className={styles.select}
                  dropdownClassName={styles.modelDropdown}
                  autoClearSearchValue={false}
                  placeholder={showPlaceholder ? modelTitle : modelPlaceholder}
                  value={getModelDisplayValue()}
                  onChange={handleModelChange}
                  open={isModelDropdownOpen}
                  onDropdownVisibleChange={handleModelDropdownVisibleChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const text = option && option.props && option.props.children;
                    return typeof text === "string" && matchesSearchText(input, text);
                  }}
                  notFoundContent={"Nema rezultata"}
                  getPopupContainer={getPopupContainer}
                  disabled={selectedMakeIds.length === 0}
                >
                  {groupedModels.map(group => (
                    <OptGroup key={group.makeId} label={group.makeName}>
                      {group.models.map(model => (
                        <Option key={model.id} value={model.id}>
                          {model.name}
                        </Option>
                      ))}
                    </OptGroup>
                  ))}
                </Select>
              </div>
            </Col>
        )}
      </Row>
    </div>
  );
};

export default MultiMakeSelector;



// WEBPACK FOOTER //
// ./src/components/FormControls/MultiMakeSelector/MultiMakeSelector.tsx